import { GET_ONBOARDING_V2_VARIANT } from '@/components/constants/flags';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { OnboardingSubVariant } from '@/models/analytics/features/onboarding';
import { useCallback, useRef, useState } from 'react';

import { track } from '@/lib/analytics/track';
import type { PersonalizationStep } from '@heidi/growth/contracts';

interface UseOnboardingAnalyticsOptions {
  /**
   * User is in the cohort the guided-tour experiment runs on at all
   * (sample-conversation learn-basics on a preview-capable viewport). Outside
   * it there is no arm to report, so `onboarding_sub_variant` is omitted rather
   * than defaulted — a `sample_conversation` value on an ineligible user would
   * inflate the control arm.
   */
  isGuidedTourEligible?: boolean;
  /** Resolved guided-tour arm within that cohort. */
  isGuidedTour?: boolean;
}

/**
 * Centralized analytics for onboarding-v2; auto-tags every event with
 * `onboarding_variant` and, for the guided-tour cohort, `onboarding_sub_variant`.
 */
export function useOnboardingAnalytics({
  isGuidedTourEligible = false,
  isGuidedTour = false,
}: UseOnboardingAnalyticsOptions = {}) {
  const flowStartTimeRef = useRef<number>(Date.now());
  const stepStartTimeRef = useRef<number>(Date.now());
  const templateGenerationStartTimeRef = useRef<number | null>(null);
  const completedStepsRef = useRef<Set<PersonalizationStep>>(new Set());

  // Variant is stable per session — capture once at mount so all callbacks share the same snapshot.
  const [onboardingVariant] = useState(() => GET_ONBOARDING_V2_VARIANT());

  // Read live rather than snapshotted: the guided-tour flag resolves
  // asynchronously from PostHog, so a mount-time snapshot would tag the early
  // steps of the funnel with the pre-resolution default. The ref keeps every
  // callback stable while still seeing the latest arm.
  const armRef = useRef<OnboardingSubVariant | undefined>(undefined);
  armRef.current = isGuidedTourEligible
    ? isGuidedTour
      ? 'guided_tour'
      : 'sample_conversation'
    : undefined;

  const variantProps = useCallback(
    () => ({
      onboarding_variant: onboardingVariant,
      ...(armRef.current && { onboarding_sub_variant: armRef.current }),
    }),
    [onboardingVariant]
  );

  const trackOnboardingStarted = useCallback(
    (params: { source?: string; region?: string }) => {
      flowStartTimeRef.current = Date.now();
      stepStartTimeRef.current = Date.now();
      track({
        event_name: TrackingEventName.OnboardingStarted,
        props: {
          source: params.source,
          region: params.region,
          ...variantProps(),
        },
      });
    },
    [variantProps]
  );

  const trackStepCompleted = useCallback(
    (
      step: PersonalizationStep,
      stepNumber: number,
      options?: { phoneNumberValid?: boolean }
    ) => {
      const durationMs = Date.now() - stepStartTimeRef.current;
      completedStepsRef.current.add(step);
      stepStartTimeRef.current = Date.now();

      track({
        event_name: TrackingEventName.OnboardingStepCompleted,
        props: {
          step,
          step_number: stepNumber,
          duration_ms: durationMs,
          ...variantProps(),
          // Only set on signup steps that render the phone-number field; omitted
          // elsewhere so the prop stays absent rather than `false`.
          ...(options?.phoneNumberValid !== undefined && {
            phone_number_valid: options.phoneNumberValid,
          }),
        },
      });
    },
    [variantProps]
  );

  const trackStepSkipped = useCallback(
    (stepName: PersonalizationStep, options?: { tasksCompletedCount?: number }) => {
      stepStartTimeRef.current = Date.now();

      track({
        event_name: TrackingEventName.OnboardingStepSkipped,
        props: {
          step_name: stepName,
          ...variantProps(),
          // GRO-3157: forward the optional depth-of-engagement dimension so
          // the note-edit skip funnel keeps the `tasks_completed_count`
          // signal it had under the old `onboarding_learn_basics_skipped`.
          ...(options?.tasksCompletedCount !== undefined && {
            tasks_completed_count: options.tasksCompletedCount,
          }),
        },
      });
    },
    [variantProps]
  );

  const trackTemplateStarted = useCallback(
    (params: { usedSuggestion: boolean; specialty?: string }) => {
      templateGenerationStartTimeRef.current = Date.now();

      track({
        event_name: TrackingEventName.OnboardingTemplateStarted,
        props: {
          used_suggestion: params.usedSuggestion,
          specialty: params.specialty,
          ...variantProps(),
        },
      });
    },
    [variantProps]
  );

  const trackTemplateCompleted = useCallback(() => {
    const durationMs = templateGenerationStartTimeRef.current
      ? Date.now() - templateGenerationStartTimeRef.current
      : 0;
    templateGenerationStartTimeRef.current = null;

    track({
      event_name: TrackingEventName.OnboardingTemplateCompleted,
      props: {
        duration_ms: durationMs,
        ...variantProps(),
      },
    });
  }, [variantProps]);

  const trackTemplateReviewed = useCallback(
    (madeChanges: boolean) => {
      track({
        event_name: TrackingEventName.OnboardingTemplateReviewed,
        props: {
          made_changes: madeChanges,
          ...variantProps(),
        },
      });
    },
    [variantProps]
  );

  const trackDocumentsSelected = useCallback(
    (params: { count: number; docTypes: string[] }) => {
      track({
        event_name: TrackingEventName.OnboardingDocumentsSelected,
        props: {
          count: params.count,
          doc_types: params.docTypes,
          ...variantProps(),
        },
      });
    },
    [variantProps]
  );

  const trackOnboardingCompleted = useCallback(() => {
    const totalDurationMs = Date.now() - flowStartTimeRef.current;
    track({
      event_name: TrackingEventName.OnboardingCompleted,
      props: {
        total_duration_ms: totalDurationMs,
        steps_completed: completedStepsRef.current.size,
        ...variantProps(),
      },
    });
  }, [variantProps]);

  const resetStepTimer = useCallback(() => {
    stepStartTimeRef.current = Date.now();
  }, []);

  // The task-level events below are the guided tour's engagement funnel: they
  // are the only signal for whether a user actually exercised detail level,
  // artefacts and evidence rather than clicking straight through. They carry the
  // variant tags for the same reason the step events do — without them the arms
  // are indistinguishable at task depth.
  const trackNoteStyleUpdated = useCallback(
    (abbreviationsEnabled: boolean) => {
      const durationMs = Date.now() - stepStartTimeRef.current;
      track({
        event_name: TrackingEventName.OnboardingNoteStyleUpdated,
        props: {
          abbreviations_enabled: abbreviationsEnabled,
          duration_ms: durationMs,
          ...variantProps(),
        },
      });
    },
    [variantProps]
  );

  // PII-safe: never log the raw query text — pre-consent flow.
  const trackEvidenceQueried = useCallback(
    (params: { queryType: 'suggested' | 'custom'; queryLength: number }) => {
      track({
        event_name: TrackingEventName.OnboardingEvidenceQueried,
        props: {
          query_type: params.queryType,
          query_length: params.queryLength,
          ...variantProps(),
        },
      });
    },
    [variantProps]
  );

  const trackEvidenceAccepted = useCallback(
    (itemsCount: number) => {
      track({
        event_name: TrackingEventName.OnboardingEvidenceAccepted,
        props: { items_count: itemsCount, ...variantProps() },
      });
    },
    [variantProps]
  );

  const trackEvidenceDiscarded = useCallback(
    (itemsCount: number) => {
      track({
        event_name: TrackingEventName.OnboardingEvidenceDiscarded,
        props: { items_count: itemsCount, ...variantProps() },
      });
    },
    [variantProps]
  );

  const trackDocumentCreated = useCallback(
    (documentType: string) => {
      track({
        event_name: TrackingEventName.OnboardingDocumentCreated,
        props: { document_type: documentType, ...variantProps() },
      });
    },
    [variantProps]
  );

  const trackLearnBasicsCompleted = useCallback(
    (tasksCompleted: string[]) => {
      const durationMs = Date.now() - stepStartTimeRef.current;
      track({
        event_name: TrackingEventName.OnboardingLearnBasicsCompleted,
        props: {
          tasks_completed: tasksCompleted,
          duration_ms: durationMs,
          ...variantProps(),
        },
      });
    },
    [variantProps]
  );

  const trackLearnBasicsSkipped = useCallback(
    (tasksCompletedCount: number) => {
      track({
        event_name: TrackingEventName.OnboardingLearnBasicsSkipped,
        props: { tasks_completed_count: tasksCompletedCount, ...variantProps() },
      });
    },
    [variantProps]
  );

  return {
    trackOnboardingStarted,
    trackStepCompleted,
    trackStepSkipped,
    trackTemplateStarted,
    trackTemplateCompleted,
    trackTemplateReviewed,
    trackDocumentsSelected,
    trackOnboardingCompleted,
    resetStepTimer,
    trackNoteStyleUpdated,
    trackEvidenceQueried,
    trackEvidenceAccepted,
    trackEvidenceDiscarded,
    trackDocumentCreated,
    trackLearnBasicsCompleted,
    trackLearnBasicsSkipped,
  };
}

export type UseOnboardingAnalyticsReturn = ReturnType<typeof useOnboardingAnalytics>;
